// ── VMS Integration Settings Types ──

export type VmsSettingType = "tenant" | "client"

/** FBO client reference — matches backend ClientDto (FboId, Name, ObCode) */
export interface VmsClientDto {
  fboId: number
  name: string
  obCode?: string | null
}

/**
 * User reference for default-assignees and similar people-shaped fields.
 * Matches the backend UserReportDto so the JSON deserializes round-trip without
 * a mapper — the same shape used by AssignedToUser on requests.
 */
export interface VmsUserReportDto {
  profileId: string
  fullName?: string
  email: string
}

/**
 * Canonical VMS type identifiers — kept in sync with the backend allowlist in
 * <c>VmsTypes</c> and the <c>detectedVmsType</c> enum the LLM parser produces.
 */
export const VMS_TYPES = [
  "Fieldglass",
  "Beeline",
  "Flextrack",
  "IQNavigator",
  "Pontoon",
  "Coupa",
  "Custom",
  "Unknown",
] as const
export type VmsTypeCode = (typeof VMS_TYPES)[number]

export interface VmsEndpointDto {
  id: string
  endpointEmail: string
  displayName: string
  isActive: boolean
  client?: VmsClientDto | null
  notes?: string | null
  lastEmailReceivedAt?: string | null
  totalEmailsReceived: number
  // LLM config (per-setting for tenant; inherited via link for client)
  llmModel?: string | null
  llmMaxTokens?: number | null
  customPromptInstructions?: string | null
  anthropicApiVersion?: string | null
  /** When true, this tenant setting is the default LLM config for its tenant. */
  isDefaultLlmConfig?: boolean
  /** For client settings: ID of the tenant setting to inherit LLM config from. */
  linkedTenantSettingsId?: string | null
  /** Profile ID of the user who created this endpoint (for delete-permission checks). */
  createdByProfileId: number
  /**
   * Stable cross-version identifier. Equal to the FIRST version's doc id, so
   * the group always traces back to a real (preserved) document. Read-only on
   * the wire.
   */
  settingsGroupId?: string | null
  /**
   * True iff this doc is the latest version of its group. Read-only on the wire.
   * The list endpoint filters historical versions out server-side so the panel
   * grid sees only current-version docs; this flag is exposed mostly for sanity
   * checks (and future "show version history" UX).
   */
  isCurrentVersion?: boolean
  /**
   * 1-based version counter within the settings group. Original is 1; every
   * semantic edit bumps this by 1. Shown as a "vN" badge in the panel header.
   */
  versionNumber?: number
  /** Operator-pinned VMS type. Null means "let the LLM decide per email". */
  vmsType?: VmsTypeCode | null
  /** Users pre-populated as AssignedToUser on requests created from this endpoint. */
  defaultAssignees?: VmsUserReportDto[]
  /**
   * When true, bypasses the global public-mail-provider gate (gmail / outlook /
   * yahoo / ~6k free providers) for emails routed through this endpoint.
   * Defaults to false — public-domain senders are parked at PendingManualCreate
   * for review instead of auto-creating a request. Enable per endpoint when
   * the endpoint legitimately accepts forwarded personal-mail traffic and
   * auto-creation is desired. Honoured by VmsEmailProcessingService against
   * either the direct setting OR the linked LLM-config setting; either flag
   * being true is sufficient.
   */
  skipPublicDomainFilter?: boolean
}

/**
 * Paginated response shape returned by GET /api/vms/config/endpoints. Items are
 * already filtered to the current version of each settings group; pagination
 * walks distinct logical settings, not raw documents.
 */
export interface PaginatedVmsSettingsResponse {
  items: VmsEndpointDto[]
  totalCount: number
  pageNumber: number
  pageSize: number
}

/** Action code on an audit row — matches the backend SettingsAuditActions struct. */
export type SettingsAuditAction =
  | "settings_created"
  | "settings_updated"
  | "settings_versioned"
  | "settings_deleted"

/** One field-level diff inside an audit row. */
export interface SettingsAuditDifferenceDto {
  fieldName: string
  displayName?: string | null
  oldValue?: unknown
  newValue?: unknown
}

/**
 * One row in a settings group's audit timeline. Returned from
 * GET /api/vms/config/endpoints/{settingsGroupId}/audit, oldest first.
 */
export interface SettingsAuditEntryDto {
  id: string
  action: SettingsAuditAction
  comment?: string | null
  createdDateTime: string
  createdByProfileId: number
  createdByUserName?: string | null
  /** Doc id at the moment of the action (specific version pointer). */
  entityGuid?: string | null
  differences: SettingsAuditDifferenceDto[]
}

export interface VmsEndpointStatsDto {
  endpointId: string
  totalEmailsReceived: number
  lastEmailReceivedAt?: string | null
  isActive: boolean
  health: "healthy" | "inactive"
}

export interface CreateVmsSettingsDto {
  displayName: string
  client?: VmsClientDto | null
  notes?: string | null
  vmsType?: VmsTypeCode | null
  defaultAssignees?: VmsUserReportDto[]
  // LLM fields (tenant settings only; ignored on client settings server-side)
  llmModel?: string | null
  llmMaxTokens?: number | null
  customPromptInstructions?: string | null
  isDefaultLlmConfig?: boolean
  /** See <see cref="VmsEndpointDto.skipPublicDomainFilter"/>. */
  skipPublicDomainFilter?: boolean
}

export interface UpdateEndpointDto {
  displayName?: string
  isActive?: boolean
  client?: VmsClientDto | null
  notes?: string | null
  llmModel?: string | null
  llmMaxTokens?: number | null
  customPromptInstructions?: string | null
  isDefaultLlmConfig?: boolean
  linkedTenantSettingsId?: string | null
  /** Empty string clears the pin. Omit to leave the current value untouched. */
  vmsType?: VmsTypeCode | null | ""
  /** Empty array clears. Omit (undefined) to leave the existing list untouched. */
  defaultAssignees?: VmsUserReportDto[]
  /** See <see cref="VmsEndpointDto.skipPublicDomainFilter"/>. */
  skipPublicDomainFilter?: boolean
}

// ── LLM / Prompt Configuration (global defaults from appsettings) ──

export interface PromptConfigDto {
  model: string
  maxTokens: number
  anthropicApiVersion?: string
  provider: string
  customPromptInstructions?: string | null
}

export const ALLOWED_LLM_MODELS = [
  "claude-sonnet-5",
  "claude-sonnet-4-6",
  "claude-haiku-4-5",
] as const

export type AllowedLlmModel = (typeof ALLOWED_LLM_MODELS)[number]

export const MAX_TOKENS_CAP = 8192
export const DEFAULT_MAX_TOKENS = 4096

// ── Helpers ──

/** Determine whether a setting is tenant-level or client-specific */
export function getSettingType(endpoint: VmsEndpointDto): VmsSettingType {
  return endpoint.client ? "client" : "tenant"
}
